import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert } from '@mui/material';
import { Placar, SetPlacar, normalizeSets, calculateFinalResults } from '../../types/placar';
import { criarPlacar, atualizarPlacar } from '../../services/placarService';
import ScoreboardGrid from './ScoreboardGrid';
import { BeachTennisScoreValidator, ValidacaoPlacarResult, PartidaMinima } from '../../utils/beachTennisScoreValidator';

interface DefineScoreDialogProps {
  open: boolean;
  partidaId: string;
  placarAtual?: Placar & { id?: string } | null;
  numeroSets: number;
  gamesPorSets?: number | null;
  tiebreaks?: number | null;
  categoriaTorneioId?: string;
  categoriaTorneio?: {
    numeroSets: number | null;
    gamesPorSets: number | null;
    tiebreaks: number | null;
  } | null;
  onClose: () => void;
  onSuccess: () => void;
}

const DefineScoreDialog: React.FC<DefineScoreDialogProps> = ({
  open,
  partidaId,
  placarAtual,
  numeroSets,
  gamesPorSets,
  tiebreaks,
  categoriaTorneioId,
  categoriaTorneio,
  onClose,
  onSuccess,
}) => {
  const [sets, setSets] = useState<SetPlacar[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validacaoPlacar, setValidacaoPlacar] = useState<ValidacaoPlacarResult | null>(null);

  // Inicializar sets quando o dialog abrir ou placarAtual mudar
  useEffect(() => {
    if (open) {
      if (placarAtual) {
        const normalizedSets = normalizeSets(placarAtual.sets, numeroSets);
        setSets(normalizedSets);
      } else {
        // Inicializar com array vazio baseado em numeroSets
        const initialSets = Array.from({ length: numeroSets }, (_, i) => ({
          numeroSet: i + 1,
          gamesA: null,
          gamesB: null,
          tiebreakA: null,
          tiebreakB: null,
        }));
        setSets(initialSets);
      }
      setError(null);
    }
  }, [open, placarAtual, numeroSets]);

  // Validar placar quando sets mudarem
  useEffect(() => {
    const validarPlacarAtual = () => {
      const partidaParaValidar: PartidaMinima = {
        categoriaTorneio: {
          numeroSets: numeroSets ?? null,
          gamesPorSets: gamesPorSets ?? null,
          tiebreaks: tiebreaks ?? null,
        },
        numeroSets: numeroSets ?? null,
        gamesPorSets: gamesPorSets ?? null,
        tiebreaks: tiebreaks ?? null,
        placar: {
          sets: sets,
        },
      };

      try {
        const resultado = BeachTennisScoreValidator.validarPlacar(partidaParaValidar);
        setValidacaoPlacar(resultado);
      } catch (error) {
        console.error('Erro ao validar placar:', error);
        setValidacaoPlacar(null);
      }
    };

    if (open && sets.length > 0) {
      validarPlacarAtual();
    }
  }, [sets, numeroSets, gamesPorSets, tiebreaks, open]);

  const handleSetFieldUpdate = (
    numeroSet: number,
    campo: 'gamesA' | 'gamesB' | 'tiebreakA' | 'tiebreakB',
    value: number | null
  ) => {
    setSets((prevSets) => {
      const newSets = [...prevSets];
      const index = newSets.findIndex((s) => s.numeroSet === numeroSet);
      if (index === -1) return prevSets;
      
      const updatedSet = {
        ...newSets[index],
        [campo]: value,
      };
      
      // Se estamos atualizando gamesA ou gamesB, verificar se o set ainda precisa de tiebreak
      // Se não precisar, limpar tiebreakA e tiebreakB
      if (campo === 'gamesA' || campo === 'gamesB') {
        const deveTerTiebreak = BeachTennisScoreValidator.deveTerTiebreak(
          updatedSet,
          gamesPorSets,
          tiebreaks
        );
        
        if (!deveTerTiebreak) {
          updatedSet.tiebreakA = null;
          updatedSet.tiebreakB = null;
        }
      }
      
      newSets[index] = updatedSet;
      return newSets;
    });
  };

  const handleSave = async () => {
    // Validar placar antes de salvar
    if (validacaoPlacar && !validacaoPlacar.valido) {
      setError(validacaoPlacar.mensagem || 'O placar contém erros. Verifique os valores preenchidos.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Limpar campos __typename se existirem
      const setsLimpos = sets.map((set: any) => {
        const { __typename, ...rest } = set;
        return rest;
      });

      // Calcular resultado final
      const { resultadoFinalA, resultadoFinalB } = calculateFinalResults(setsLimpos);

      const placarInput = {
        partidaId,
        sets: setsLimpos,
        resultadoFinalA,
        resultadoFinalB,
      };

      // Usar criarPlacar ou atualizarPlacar baseado na existência do placar
      if (placarAtual?.id) {
        await atualizarPlacar(placarAtual.id, placarInput);
      } else {
        await criarPlacar(placarInput);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erro ao salvar placar:', err);
      setError(err.message || 'Erro ao salvar placar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Definir Resultado</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {error}
          </Alert>
        )}
        <ScoreboardGrid
          sets={sets}
          numeroSets={numeroSets}
          categoriaTorneio={categoriaTorneio}
          gamesPorSets={gamesPorSets}
          tiebreaks={tiebreaks}
          canEdit={true}
          onSetFieldUpdate={handleSetFieldUpdate}
          camposInvalidos={validacaoPlacar?.camposInvalidos}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DefineScoreDialog;

